<template>
  <div class="flex-1 flex flex-col min-w-0 min-h-0 overflow-auto">
    <div class="flex items-start justify-between gap-4 mb-4">
      <div
        v-if="canSave && canIssueTenders"
        class="flex items-end gap-2 p-4"
      >
        <div class="min-w-[220px]">
          <label class="block text-xs font-medium text-gray-600 mb-1">
            Subcontractor
          </label>

          <AppTypeAhead
            v-model="supplierName"
            name="supplier_id"
            :suggestion-callback="getSuggestions"
            :initial-suggestions="getSuggestions"
            class="form-input"
            placeholder="Search subcontractors..."
            data-test="tender-supplier-select"
            @value-selected="selectSupplier"
          />
        </div>

        <button
          type="button"
          class="btn btn--primary-blue"
          :disabled="!selectedSupplierId || issueAction.loading.value || !canIssueTenders"
          data-test="issue-tenders"
          @click="issueTender()"
        >
          Issue Tender
        </button>
      </div>

      <div class="flex items-center gap-2 p-4 ml-auto">
        <span
          v-if="isProcurementComplete"
          class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800"
        >
          Complete
        </span>

        <button
          v-if="canIssueTenders"
          type="button"
          class="btn btn--secondary-blue flex items-center space-x-2"
          :class="{ 'opacity-50': exportAction.is(States.LOADING) }"
          :disabled="exportAction.is(States.LOADING)"
          data-test="export-tender-evaluation"
          @click="exportTenderEvaluation"
        >
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" class="w-4 h-4">
<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
</svg>

          <span>Export to PDF</span>
        </button>
      </div>
    </div>

    <div
      v-if="indexAction.loading.value"
      class="flex justify-center py-12"
    >
      <AppSpinner />
    </div>

    <div
      v-else-if="canIssueTenders"
      class="overflow-x-auto"
    >
      <table class="min-w-full divide-y divide-gray-200 text-sm">
        <thead class="bg-gray-50">
          <tr>
            <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase sticky left-0 bg-gray-50">
              Package Item
            </th>

            <th
              v-if="showWbsCode"
              class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase"
            >
              WBS Code
            </th>

            <th class="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">
              Qty
            </th>

            <th
              v-if="!isLumpSum"
              class="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase"
            >
              Tender Rate
            </th>

            <th class="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">
              Budget
            </th>

            <template
              v-for="tender in latestTenders"
              :key="`head-${tender.id}`"
            >
              <th
                :colspan="tenderColspan"
                class="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase border-l w-1/4"
              >
                <div>{{ tender.supplier?.name }}</div>

                <div class="mt-1 flex items-center justify-center gap-2 font-normal normal-case">
                  <span class="text-gray-400">
                    Rev {{ tender.price_revision }}
                  </span>

                  <span
                    class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                    :class="tender.statusBadgeClass"
                  >
                    {{ tender.statusTitle }}
                  </span>
                </div>

                <div
                  v-if="tender.submittedAt"
                  class="mt-1 text-xs font-normal normal-case text-gray-400"
                >
                  Submitted {{ tender.submittedAt.toLocaleString() }}
                </div>

                <div class="flex items-center justify-center gap-2 mt-1">
                  <button
                    v-if="canSave && tender.status === 'submitted' && selectedPackage.subcontractor_id !== tender.supplier_id"
                    type="button"
                    class="btn btn--small btn--secondary-blue"
                    :disabled="selectWinnerAction.loading.value"
                    @click="selectWinner(tender)"
                  >
                    Select
                  </button>

                  <span
                    v-else-if="selectedPackage.subcontractor_id === tender.supplier_id"
                    class="mt-1 text-green-600 normal-case"
                  >
                    Selected
                  </span>

                  <button
                    v-if="canSave && falsey(selectedPackage.subcontractor_id)"
                    type="button"
                    class="btn btn--small btn--secondary-blue"
                    :disabled="issueAction.loading.value"
                    @click="issueTender(tender.supplier_id)"
                  >
                    Re-issue
                  </button>
                </div>
              </th>
            </template>
          </tr>

          <tr>
            <th class="sticky left-0" />

            <th v-if="showWbsCode" />

            <th />

            <th v-if="!isLumpSum" />

            <th />

            <template
              v-for="tender in latestTenders"
              :key="`subhead-${tender.id}`"
            >
              <th
                v-if="!isLumpSum"
                class="px-3 py-1 text-right text-xs font-medium text-gray-400 border-l"
              >
                Rate
              </th>

              <th
                class="px-3 py-1 text-right text-xs font-medium text-gray-400"
                :class="{ 'border-l': isLumpSum }"
              >
                Amount
              </th>
            </template>
          </tr>
        </thead>

        <tbody class="bg-white divide-y divide-gray-200">
          <tr
            v-for="item in packageTradeItems"
            :key="item.id"
          >
            <td class="px-3 py-2 sticky left-0 bg-white">
              {{ item.trade_item?.name }}
            </td>

            <td
              v-if="showWbsCode"
              class="px-3 py-2"
            >
              {{ item.wbs_code?.code || '' }}
            </td>

            <td class="px-3 py-2 text-right">
              {{ item.quantity ?? item.trade_item?.quantity }} {{ item.trade_item?.unit }}
            </td>

            <td
              v-if="!isLumpSum"
              class="px-3 py-2 text-right"
            >
              {{ $tcc(item.trade_item?.rate) }}
            </td>

            <td class="px-3 py-2 text-right">
              {{ $tcc(item.trade_item?.budget) }}
            </td>

            <template
              v-for="tender in latestTenders"
              :key="`row-${tender.id}-${item.id}`"
            >
              <td
                v-if="!isLumpSum"
                class="px-3 py-2 text-right border-l"
              >
                {{ $tcc(itemFor(tender, item.id)?.rate) }}
              </td>

              <td
                class="px-3 py-2 text-right"
                :class="{ 'border-l': isLumpSum }"
              >
                {{ $tcc(itemFor(tender, item.id)?.amount) }}
              </td>
            </template>
          </tr>
        </tbody>

        <tfoot class="bg-gray-50">
          <tr>
            <td
              class="px-3 py-2 font-semibold sticky left-0 bg-gray-50"
              :colspan="fixedColumnsBeforeBudget"
            >
              Total / Rank
            </td>

            <td class="px-3 py-2 text-right">
              {{ $tcc(totalBudget) }}
            </td>

            <template
              v-for="tender in rankedTenders"
              :key="`total-${tender.id}`"
            >
              <td
                class="px-3 py-2 text-right font-semibold border-l"
                :colspan="tenderColspan"
              >
                {{ $tcc(tender.totalAmount) }}
                <span
                  v-if="tender.rank"
                  class="text-gray-500 font-normal"
                >
                  (Rank {{ tender.rank }})
                </span>
              </td>
            </template>
          </tr>
        </tfoot>
      </table>
    </div>
  </div>
</template>

<script setup lang="ts">
import { States, useStoreApiAction } from '@/composables/useStoreApiAction';
import { falsey } from '@/helpers';
import Package from '@/models/Package';
import PackageTender from '@/models/PackageTender';
import { SupplierType } from '@/models/ProjectSupplier';
import { usePackageProcurementStepsStore } from '@/store/packageProcurementSteps';
import { usePackageTendersStore } from '@/store/packageTenders';
import { usePackageTradeItemsStore } from '@/store/packageTradeItems';
import { useProjectsStore } from '@/store/projects';
import { useProjectSupplierStore } from '@/store/projectSupplier';
import { useSuppliersStore } from '@/store/supplier';
import Swal from 'sweetalert2';
import { computed, nextTick, ref, watch } from 'vue';

const props = defineProps<{
  selectedPackage: Package;
  canSave: boolean;
}>();

const projectsStore = useProjectsStore();
const packageTendersStore = usePackageTendersStore();
const packageTradeItemsStore = usePackageTradeItemsStore();
const packageProcurementStepsStore = usePackageProcurementStepsStore();
const projectSuppliersStore = useProjectSupplierStore();
const suppliersStore = useSuppliersStore();
const indexAction = useStoreApiAction(packageTendersStore.index);
const issueAction = useStoreApiAction(packageTendersStore.issue);
const selectWinnerAction = useStoreApiAction(packageTendersStore.selectWinner);
const exportAction = useStoreApiAction(packageTendersStore.export);
const stepsIndexAction = useStoreApiAction(packageProcurementStepsStore.index);
const supplierName = ref('');
const selectedSupplierId = ref<number>();
const suggestedSuppliers = ref<{ id: number; name: string }[]>([]);
const isLumpSum = computed(() => props.selectedPackage.procurement_method === 'lump_sum');
const tenderColspan = computed(() => (isLumpSum.value ? 1 : 2));

const showWbsCode = computed(() => {
  return packageTradeItems.value.some((item) => !!item.wbs_code_id || !!item.wbs_code?.code);
});

const fixedColumnsBeforeBudget = computed(() => {
  return 2 + (showWbsCode.value ? 1 : 0) + (isLumpSum.value ? 0 : 1);
});

const projectSuppliers = computed(() => {
  return projectSuppliersStore.models
    .where('project_id', projectsStore.project?.id)
    .where('type', SupplierType.subcontractor)
    .get();
});

const getSuggestions = async () => {
  suggestedSuppliers.value = await suppliersStore.search({
    name: supplierName.value,
    project_id: projectsStore.project.id,
    type: [SupplierType.subcontractor],
  });

  return suggestedSuppliers.value.map((supplier) => supplier.name);
};

const selectSupplier = (index: number) => {
  nextTick(() => {
    const supplier = suggestedSuppliers.value[index];

    if(!supplier?.id) {
      return;
    }

    selectedSupplierId.value = supplier.id;
  });
};

const contactEmailFor = (supplierId: number) => {
  return projectSuppliers.value.find((row) => row.supplier_id === supplierId)?.contact_email ?? '';
};

const promptForEmail = async (supplierId: number, isReissue: boolean) => {
  const supplierEmail = contactEmailFor(supplierId);

  const result = await Swal.fire({
    title: isReissue ? 'Re-issue Tender' : 'Issue Tender',
    html: `
      <div class="text-left">
        <p class="mb-4">This tender will be sent to the subcontractor via email.</p>
        <p class="mb-4 text-sm text-gray-600">
          <strong>Default email:</strong> ${supplierEmail || 'No contact email set'}
        </p>
        <p class="mb-4 text-sm text-gray-600">
          You can override the email address below if needed.
        </p>
      </div>
      <input
        id="email-input"
        type="email"
        class="swal2-input"
        placeholder="Override email (optional)"
        value="${supplierEmail || ''}"
      />
    `,

    showCancelButton: true,
    confirmButtonText: isReissue ? 'Re-issue Tender' : 'Issue Tender',
    cancelButtonText: 'Cancel',
    preConfirm: () => {
      const emailInput = document.getElementById('email-input') as HTMLInputElement;
      const email = emailInput?.value?.trim();

      if(email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        Swal.showValidationMessage('Please enter a valid email address');

        return false;
      }

      return email || undefined;
    },

    allowOutsideClick: false,
    allowEscapeKey: true,
  });

  if(!result.isConfirmed) {
    return undefined;
  }

  return result.value || undefined;
};

const packageTradeItems = computed(() => {
  return packageTradeItemsStore.models
    .where('package_id', props.selectedPackage.id)
    .with('trade_item')
    .with('wbs_code')
    .get();
});

const totalBudget = computed(() => {
  return packageTradeItems.value.reduce((total, item) => {
    return total + item.trade_item?.budget;
  }, 0);
});

const packageTenders = computed(() => {
  return packageTendersStore.models
    .where('package_id', props.selectedPackage.id)
    .with('supplier')
    .with('items')
    .get();
});

const latestTenders = computed(() => {
  const packageTendersBySupplierId = new Map<number, PackageTender>();

  packageTenders.value.forEach((tender) => {
    const existing = packageTendersBySupplierId.get(tender.supplier_id);

    if(!existing || tender.price_revision > existing.price_revision) {
      packageTendersBySupplierId.set(tender.supplier_id, tender);
    }
  });

  return Array.from(packageTendersBySupplierId.values());
});

const rankedTenders = computed(() => {
  const submitted = latestTenders.value
    .filter((tender) => tender.status === 'submitted')
    .sort((a, b) => a.totalAmount - b.totalAmount);

  return latestTenders.value.map((tender) => ({
    ...tender,
    totalAmount: tender.totalAmount,
    rank: tender.status === 'submitted' ?
      submitted.findIndex((row) => row.id === tender.id) + 1 :
      undefined,
  }));
});

const itemFor = (tender: PackageTender, packageTradeItemId: number) => {
  return tender.items?.find((item) => item.package_trade_item_id === packageTradeItemId);
};

const packageSteps = computed(() => {
  return packageProcurementStepsStore.models
    .where('package_id', props.selectedPackage.id)
    .orderBy('sort_order')
    .get();
});

const lastStepComplete = computed(() => {
  if(!packageSteps.value.length) {
    return false;
  }

  return !!packageSteps.value[packageSteps.value.length - 1].actual_date;
});

const isProcurementComplete = computed(() => {
  return !!props.selectedPackage.subcontractor_id && lastStepComplete.value;
});

const loadTenders = () => {
  if(!projectsStore.project?.id || !props.selectedPackage?.id) {
    return;
  }

  indexAction.request(projectsStore.project.id, props.selectedPackage.id).catch(() => {
    Swal.fire({
      icon: 'error',
      title: 'Failed to load tender evaluation',
    });
  });
};

const loadSteps = () => {
  if(!projectsStore.project?.id || !props.selectedPackage?.id) {
    return;
  }

  stepsIndexAction.request(projectsStore.project.id, props.selectedPackage.id);
};

const issueTender = async (supplierId?: number) => {
  const id = supplierId ?? selectedSupplierId.value;
  const isReissue = supplierId !== undefined;

  if(!id || !projectsStore.project?.id) {
    return;
  }

  const email = await promptForEmail(id, isReissue);

  if(email === undefined) {
    return;
  }

  issueAction.request(
    projectsStore.project.id,
    props.selectedPackage.id,
    {
      supplier_id: id,
      email,
    },
  ).then(() => {
    if(!isReissue) {
      supplierName.value = '';
      selectedSupplierId.value = undefined;
    }

    Swal.fire({
      icon: 'success',
      title: isReissue ? 'Tender re-issued' : 'Tender issued',
    });
  }).catch((error: any) => {
    Swal.fire({
      icon: 'error',
      title: 'Failed to issue tender',
      text: error?.data?.message,
    });
  });
};

const selectWinner = (tender: PackageTender) => {
  Swal.fire({
    title: 'Select Winner',
    text: `Are you sure you want to select ${tender.supplier?.name} as the winner?`,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: 'Select',
    cancelButtonText: 'Cancel',
  }).then((result) => {
    if(result.isConfirmed) {
      selectWinnerAction.request(
        projectsStore.project.id,
        props.selectedPackage.id,
        { package_tender_id: tender.id },
      ).then(() => {
        loadSteps();

        Swal.fire({
          icon: 'success',
          title: 'Subcontractor selected',
          text: 'Awarded values have been populated from the tender.',
        });
      });
    }
  });
};

const exportTenderEvaluation = () => {
  if(!projectsStore.project?.id || !props.selectedPackage?.id) {
    return;
  }

  exportAction.request(projectsStore.project.id, props.selectedPackage.id).then(() => {
    Swal.fire({
      icon: 'success',
      title: 'Tender Evaluation Exported.',
      text: 'Tender evaluation is being exported to PDF and will be sent to your email when it is ready.',
    });
  }).catch((error: any) => {
    if(error.status === 429) {
      Swal.fire({
        icon: 'error',
        title: 'PDF is currently being exported',
        text: error.data.message,
      });
    } else {
      Swal.fire({
        icon: 'error',
        title: 'Failed to Export.',
      });
    }
  });
};

watch(() => props.selectedPackage.id, () => {
  loadTenders();
  loadSteps();
}, { immediate: true });

const canIssueTenders = computed(() => {
  return props.selectedPackage.procurement_method && packageTradeItems.value.length;
});
</script>
